import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { useEffect, useRef, useState } from 'react';
import { DeleteStationDialog } from './components/DeleteStationDialog';
import { DeleteStationsDialog } from './components/DeleteStationsDialog';
import { StationDialog } from './components/StationDialog';
import { useStationDialogs } from '../../hooks/Stations/useStationDialogs';
import { useStationsData } from '../../hooks/Stations/useStationsData';
import { Station } from '../../types/station';
import { CustomButton, CustomTableField } from '../_shared/CustomComponents';
import DataTableFilterElements from '../_shared/DataTableFilterElements';
import getPaginatorTemplate from '../_shared/getPaginatorTemplate';
import GenerateAnprApiKeyDialog from './components/GenerateAnprApiKeyDialog';
import { Message } from 'primereact/message';
import { ObserveSiteActions } from './components/ObserveSiteActions';
import observeService, { ObserveApp } from '../../services/ScaleoAdminApiServices/ObserveService';

export const Stations = () => {
  const dt = useRef(null);

  const {
    fetchStations,
    stations,
    handleSort,
    paginationParameters,
    paginatorState,
    setPaginatorState,
    internalFilters,
    changeFilter,
    clearFilters,
    locations,
    filters,
    licenses,
  } = useStationsData();

  const {
    setSelectedStations,
    openNew,
    confirmDeleteSelected,
    hideDeleteStationDialog,
    deleteStation,
    deleteSelectedStations,
    editStation,
    hideDeleteStationsDialog,
    setStation,
    setDeleteStationDialogVisible,
    setStationDialogVisible,
    stationDialogVisible,
    deleteStationDialogVisible,
    deleteStationsDialogVisible,
    station,
    selectedStations,
    isOpenGenerateKeyDialog,
    setIsOpenGenerateKeyDialog,
  } = useStationDialogs(fetchStations);

  const [observeError, setObserveError] = useState<string | null>(null);
  const [observeSyncNotice, setObserveSyncNotice] = useState<string | null>(null);
  const [observeApp, setObserveApp] = useState<ObserveApp | null>(null);

  // Cobrick's address differs per environment, so the link target comes from the backend.
  useEffect(() => {
    observeService
      .getApp()
      .then(({ data }) => setObserveApp(data))
      .catch(() => setObserveApp(null));
  }, []);

  // createStation/updateStation report a failed Observe provisioning without failing the request
  // itself (the station is still created) - surface it here instead of silently dropping it.
  const [stationSavedObserveError, setStationSavedObserveError] = useState<string | null>(null);

  const handleStationAdded = (savedStation: Station) => {
    setStationSavedObserveError(savedStation?.observeError ?? null);
    void fetchStations();
  };

  const observeSiteUrl = (siteName: string) =>
    observeApp ? `${observeApp.appUrl}/admin/users?scope=${observeApp.scope}.${siteName}` : null;

  const header = (
    <div className="table-header">
      {'Stanowiska'} &#160;&#160;
      <div className="p-text-right">
        <Button
          label={'Wyczyść'}
          icon="pi pi-filter-slash"
          className="p-button-outlined p-mr-2"
          onClick={clearFilters}
        />
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <DataTableFilterElements.Text
            initialValue={internalFilters['global']}
            onChangeFilter={changeFilter}
            name={'global'}
            placeholder={'Wyszukaj'}
          />
        </span>
      </div>
    </div>
  );

  const leftToolbarTemplate = () => {
    return (
      <>
        <Button label={'Dodaj'} icon="pi pi-plus" className="p-button-success p-mr-2" onClick={openNew} />
        <CustomButton
          label={'Usuń'}
          icon="pi pi-trash"
          className="p-button-danger"
          onClick={confirmDeleteSelected}
          disabled={!selectedStations || !selectedStations.length}
          tooltip={!selectedStations.length ? 'Wybierz' : 'Nie można usunąć'}
          name={undefined}
          dataFlag={undefined}
          type={undefined}
        />
      </>
    );
  };

  const actionBodyTemplate = (rowData: Station) => {
    return (
      <div className="actions" style={{ width: 'max-content', float: 'right' }}>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success p-mr-2"
          onClick={() => editStation(rowData)}
        />
        <ObserveSiteActions
          station={rowData}
          onChanged={fetchStations}
          onError={setObserveError}
          onSyncNotice={setObserveSyncNotice}
        />
        <Button
          icon="pi pi-key"
          className="p-button-rounded p-button-error p-mr-2"
          onClick={() => {
            setStation(rowData);
            setIsOpenGenerateKeyDialog(true);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-secondary"
          onClick={() => {
            setStation(rowData);
            setDeleteStationDialogVisible(true);
          }}
        />
      </div>
    );
  };

  const columnBodyTemplate = (rowData: Station, column: any) => {
    return <CustomTableField row={rowData} col={column} withoutHeaders={undefined} />;
  };

  const observeSiteBodyTemplate = (rowData: Station) => {
    const url = rowData.observeSite ? observeSiteUrl(rowData.observeSite) : null;

    return (
      <>
        <span className="p-column-title">{'Site Observe'}</span>
        <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          {url ? (
            <a href={url} target="_blank" rel="noreferrer" title={'Otwórz w Cobrick Observe'}>
              {rowData.observeSite}
            </a>
          ) : (
            rowData.observeSite
          )}
        </div>
      </>
    );
  };

  const locationBodyTemplate = (rowData: Station) => (
    <>
      <span className="p-column-title">{'Oddział'}</span>
      {locations?.find((l) => l.id === rowData.location?.id)?.name}
    </>
  );

  const customerBodyTemplate = (rowData: Station) => {
    const location = locations?.find((l) => l.id === rowData.location?.id);

    return (
      <>
        <span className="p-column-title">{'Klient'}</span>
        {licenses?.find((l) => l.id === location?.customer?.id)?.companyName}
      </>
    );
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="card">
          <Toolbar left={leftToolbarTemplate} />
          {observeError && (
            <Message severity="error" text={`Cobrick Observe: ${observeError}`} className="p-mb-2 p-d-block" />
          )}
          {observeSyncNotice && <Message severity="info" text={observeSyncNotice} className="p-mb-2 p-d-block" />}
          {stationSavedObserveError && (
            <Message
              severity="warn"
              text={`Stanowisko zapisane, ale nie udało się utworzyć site'a w Cobrick Observe: ${stationSavedObserveError}. Ponów przyciskiem pobierania konfiguracji.`}
              className="p-mb-2 p-d-block"
            />
          )}
          <div className="datatable-responsive">
            <div className="card datatable-card">
              <DataTable
                ref={dt}
                value={stations ?? []}
                header={header}
                className="p-datatable-responsive"
                dataKey="id"
                rowHover
                selection={selectedStations}
                selectionMode="checkbox"
                onSelectionChange={(e) => setSelectedStations(e.value)}
                emptyMessage={'Brak danych'}
                removableSort
                sortOrder={paginationParameters.order.isAscending ? 1 : -1}
                sortField={paginationParameters.order.orderColumn}
                onSort={handleSort}
                paginator
                rows={paginatorState.rows}
                paginatorTemplate={getPaginatorTemplate(paginatorState, setPaginatorState) as any}
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                <Column
                  key={'name'}
                  field={'name'}
                  header={'Nazwa'}
                  body={columnBodyTemplate}
                  fieldsType={'text'}
                  sortable
                  filter
                  filterElement={
                    <DataTableFilterElements.Text
                      initialValue={internalFilters['name']}
                      onChangeFilter={changeFilter}
                      name={'name'}
                      placeholder={'Filtruj'}
                    />
                  }
                />
                <Column
                  key={'installationId'}
                  field={'installationId'}
                  header={'Identyfikator instalacji'}
                  body={columnBodyTemplate}
                  fieldsType={'text'}
                  sortable
                  filter
                  filterElement={
                    <DataTableFilterElements.Text
                      initialValue={internalFilters['installationId']}
                      onChangeFilter={changeFilter}
                      name={'installationId'}
                      placeholder={'Filtruj'}
                    />
                  }
                />
                <Column
                  key={'observeSite'}
                  field={'observeSite'}
                  header={'Site Observe'}
                  body={observeSiteBodyTemplate}
                />
                <Column
                  field="customerName"
                  header={'Licencja'}
                  sortable
                  body={customerBodyTemplate}
                  filter
                  filterElement={
                    <DataTableFilterElements.Text
                      initialValue={filters['customerName']}
                      onChangeFilter={changeFilter}
                      name="customerName"
                      placeholder={'filtruj'}
                    />
                  }
                  filterPlaceholder={'Wyszkuj przez klienta'}
                />
                <Column
                  field="locationId"
                  header={'Oddział'}
                  sortable
                  body={locationBodyTemplate}
                  filter
                  filterElement={
                    <DataTableFilterElements.Text
                      initialValue={filters['locationName']}
                      onChangeFilter={changeFilter}
                      name="locationName"
                      placeholder={'filtruj'}
                    />
                  }
                  filterPlaceholder={'Wyszkuj przez oddział'}
                />
                <Column
                  key={'vpnAddress'}
                  field={'vpnAddress'}
                  header={'Adresacja VPN'}
                  body={columnBodyTemplate}
                  fieldsType={'text'}
                />
                <Column body={actionBodyTemplate}></Column>
              </DataTable>
              <StationDialog
                visible={stationDialogVisible}
                onStationAdded={handleStationAdded}
                setStationDialogVisible={setStationDialogVisible}
                station={station}
                locations={locations}
                licenses={licenses}
              />
              <DeleteStationDialog
                deleteStationDialogVisible={deleteStationDialogVisible}
                hideDeleteStationDialog={hideDeleteStationDialog}
                station={station}
                deleteStation={deleteStation}
              />
              <DeleteStationsDialog
                deleteStationsDialogVisible={deleteStationsDialogVisible}
                hideDeleteStationsDialog={hideDeleteStationsDialog}
                deleteSelectedStations={deleteSelectedStations}
              />
              <GenerateAnprApiKeyDialog
                visible={isOpenGenerateKeyDialog}
                closeDialog={() => {
                  setIsOpenGenerateKeyDialog(false);
                  void fetchStations();
                }}
                station={station}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stations;
